import React from 'react';
import { Box, Flex } from 'rebass/styled-components';
import { Switch, Route } from 'react-router-dom';
import PropTypes from 'prop-types';

import { ContactDetail } from 'components/organisms/contactDetail';
import { Toolbar } from 'components/molecules/agGrid/components/toolbar';
import { defaultFilters } from '../../components/organisms/contactsFilters/defaultFilters';
import useWindowSize from '../../hooks/useWindowSize';
import { ContactsSidebar } from './contactsSidebar';
import { EmptyContacts } from './emptyContacts';
import { ContactsCardView } from './contactsCardView';
import { ContactsTableView } from './contactsTableView';
import { BulkActions } from './bulkActions';
import { useExtendContacts } from './hooks/useExtendContacts';
import { ContactsKanbanView } from './components/contactsKanbanView';

export const ExtendedContact = ({
  onSetFilters,
  queryParams,
  selectedRows,
  setSelectedRows,
  onChangeSelectedContact,
  onLoadMore,
  data,
  customFields,
  isLoading,
  isFetching,
  meta,
  filtersCount,
  activeNetwork,
  showAllContactsEmptyState,
  showListContactsEmptyState,
  listId,
  showAddToLisButton,
  showFiltersBottomBar,
  emptyState,
  showListFilters,
  list,
  deleteContactFromList,
  filtersTitle,
  bulkActions,
  page,
  isLoadingAI,
  listAction,
  type,
  disabledContacts,
  otherToolbarOptions,
  tableSx,
  cardSx,
  onOpenNotesDrawer,
}) => {
  const {
    view,
    hasContacts,
    showFilters,
    setShowFilters,
    toolbarOptionChange,
    onResetFilters,
    onCreateList,
    handleSelectAll,
    linkedinSync,
    showAddNote,
    selectedColumns,
    setSelectedColumns,
    tableRef,
    paths,
    isDownloadingCsv,
    currentSort,
  } = useExtendContacts({
    data,
    customFields,
    isFetching,
    queryParams,
    isLoadingAI,
    listId,
    selectedRows,
    setSelectedRows,
    meta,
    onSetFilters,
    defaultFilters,
    activeNetwork,
    type,
    page,
    isLoading,
    list,
  });
  useWindowSize();
  const isLoadingOrFetching = isLoading || isFetching || isLoadingAI;
  const isEmptyState =
    showAllContactsEmptyState ||
    showListContactsEmptyState ||
    (!isLoadingOrFetching && data.length === 0 && filtersCount === 0);

  return (
    <>
      {!isEmptyState && (
        <Toolbar
          view={view}
          toolbarOptionChange={toolbarOptionChange}
          selectedColumns={selectedColumns}
          otherToolbarOptions={otherToolbarOptions}
          isDownloadingCsv={isDownloadingCsv}
          type={type}
          page={page}
          filtersVisible={showFilters}
          filtersCount={filtersCount}
          tableRef={tableRef}
          currentSort={currentSort}
          showLayout={type !== 'public'}
        />
      )}
      <Flex
        height={view === 'card' ? 'initial' : 'auto'}
        sx={{ position: 'relative', bg: 'neutral.0', overflowX: 'hidden' }}
      >
        {!isEmptyState && (
          <Box
            sx={{
              height: tableSx?.height ? tableSx.height : 'calc(100vh - 89px)',
              bg: 'neutral.0',
              width: '264px',
              transform: showFilters ? 'translateX(0%)' : 'translateX(-100%)',
              opacity: showFilters ? '1' : '0',
              transition: 'transform 0.3s ease-in-out, opacity 0.3s ease-in-out',
              flexShrink: 0,
              overflow: 'hidden',
              zIndex: 2,
            }}
          >
            <ContactsSidebar
              activeNetwork={activeNetwork}
              isLoadingOrFetching={isLoadingOrFetching}
              dataCount={data?.length || 0}
              filtersCount={filtersCount}
              showListFilters={showListFilters}
              queryParams={queryParams}
              onSetFilters={onSetFilters}
              list={list}
              onCreateList={onCreateList}
              onResetFilters={onResetFilters}
              showFiltersBottomBar={showFiltersBottomBar}
              filtersTitle={filtersTitle}
              toggleFilters={() => setShowFilters(!showFilters)}
              type={type}
              page={page}
              showTalentNetworkFilters={activeNetwork?.features?.includes('job_board')}
              customFields={customFields}
            />
          </Box>
        )}
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            minHeight: 'calc(100dvh - 203px)',
            minWidth: ['100vw', '100vw', 0],
            transition: 'margin-left 0.3s ease-in-out',
            marginLeft: isEmptyState || showFilters ? '0px' : '-268px',
          }}
        >
          {view === 'card' && (isLoadingOrFetching || (!isLoadingOrFetching && data.length > 0)) && (
            <ContactsCardView
              isLoadingOrFetching={isLoadingOrFetching}
              queryParams={queryParams}
              selectedRows={selectedRows}
              onChangeSelectedContact={onChangeSelectedContact}
              onLoadMore={onLoadMore}
              data={data}
              isLoading={isLoading}
              isFetching={isFetching}
              meta={meta}
              filtersCount={filtersCount}
              activeNetwork={activeNetwork}
              showAllContactsEmptyState={showAllContactsEmptyState}
              listId={listId}
              showAddToLisButton={showAddToLisButton}
              list={list}
              deleteContactFromList={deleteContactFromList}
              bulkActions={bulkActions}
              page={page}
              isLoadingAI={isLoadingAI}
              linkedinSync={linkedinSync}
              handleSelectAll={handleSelectAll}
              listAction={listAction}
              type={type}
              disabledContacts={disabledContacts}
              cardSx={cardSx}
            />
          )}
          {view === 'table' && (isLoadingOrFetching || (!isLoadingOrFetching && data.length > 0)) && (
            <ContactsTableView
              view={view}
              data={data}
              activeNetwork={activeNetwork}
              listId={listId}
              list={list}
              filtersCount={filtersCount}
              onSetFilters={onSetFilters}
              onSaveFilters={() => {}}
              savedFilters={[]}
              isSavingFilters={false}
              columns={selectedColumns}
              filtersVisible={false}
              queryParams={queryParams}
              page={page}
              deleteContactFromList={deleteContactFromList}
              addedToSharedList={false}
              showSkeleton={
                ((isFetching || isLoadingAI) && queryParams.page === 1) || (isLoading && queryParams.page === 1)
              }
              setColumns={setSelectedColumns}
              selectedAll={selectedRows.length === meta?.queryTotal}
              onSelectAll={handleSelectAll}
              onChangeSelectedContact={onChangeSelectedContact}
              selectedRows={selectedRows}
              onLoadMore={onLoadMore}
              hasMore={data?.length < meta?.queryTotal}
              isLoadingMore={isFetching}
              ref={tableRef}
              meta={meta}
              type={type}
              disabledContacts={disabledContacts}
              sx={tableSx}
              onOpenNotesDrawer={onOpenNotesDrawer}
              hasBulkActions={selectedRows.length > 0}
            />
          )}
          {view === 'board' && (
            <ContactsKanbanView
              queryParams={queryParams}
              isLoadingData={isLoadingOrFetching}
              list={list}
              customFields={customFields}
            />
          )}
          {!isLoadingOrFetching && data.length === 0 && view !== 'board' && (
            <EmptyContacts
              showAllContactsEmptyState={showAllContactsEmptyState}
              showListContactsEmptyState={showListContactsEmptyState}
              isLoading={isLoading}
              isFetching={isFetching}
              dataCount={data?.length || 0}
              listId={listId}
              queryParams={queryParams}
              defaultFilters={defaultFilters}
              emptyState={emptyState}
              type={type}
            />
          )}
        </Box>
        {hasContacts && type !== 'public' && (
          <BulkActions
            actions={bulkActions}
            sharedList={list}
            queryParams={queryParams}
            contactIds={data?.map((contact) => contact.id)}
            selectedRows={selectedRows}
            meta={meta}
            setSelectedRows={setSelectedRows}
            page={page}
            onClearSelection={() => {
              tableRef.current?.api?.deselectAll?.();
            }}
          />
        )}
        <Switch>
          {paths.map(({ route: path, goBack }) => (
            <Route path={path}>
              <ContactDetail
                showAddNote={showAddNote}
                queryParams={queryParams}
                isTalentNetworkEnabled={!activeNetwork?.tnHidden}
                list={list}
                onClose={() => {
                  goBack();
                }}
                deleteContactFromList={deleteContactFromList}
                page={page}
                showMoreOptions={type !== 'public'}
              />
            </Route>
          ))}
        </Switch>
      </Flex>
    </>
  );
};

ExtendedContact.defaultProps = {
  listId: null,
  showAddToLisButton: false,
  emptyState: null,
  showListFilters: true,
  showFiltersBottomBar: false,
  list: null,
  meta: null,
  deleteContactFromList: false,
  filtersTitle: '',
  bulkActions: ['add-to-list', 'add-tag', 'export', 'delete', 'clear-selection'],
  showListContactsEmptyState: false,
  listAction: null,
  type: 'default',
  disabledContacts: [],
  otherToolbarOptions: null,
  tableSx: {},
  cardSx: {},
  customFields: [],
  onOpenNotesDrawer: null,
};

ExtendedContact.propTypes = {
  onSetFilters: PropTypes.func.isRequired,
  queryParams: PropTypes.shape({
    page: PropTypes.number,
    filters: PropTypes.object,
  }).isRequired,
  selectedRows: PropTypes.arrayOf(PropTypes.number).isRequired,
  setSelectedRows: PropTypes.func.isRequired,
  onChangeSelectedContact: PropTypes.func.isRequired,
  onLoadMore: PropTypes.func.isRequired,
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  customFields: PropTypes.arrayOf(PropTypes.object),
  isLoading: PropTypes.bool.isRequired,
  isFetching: PropTypes.bool.isRequired,
  meta: PropTypes.object,
  filtersCount: PropTypes.number.isRequired,
  activeNetwork: PropTypes.object.isRequired,
  showAllContactsEmptyState: PropTypes.bool.isRequired,
  listId: PropTypes.number,
  showAddToLisButton: PropTypes.bool,
  showFiltersBottomBar: PropTypes.bool,
  emptyState: PropTypes.node,
  showListFilters: PropTypes.bool,
  list: PropTypes.object,
  deleteContactFromList: PropTypes.bool,
  filtersTitle: PropTypes.string,
  bulkActions: PropTypes.array,
  page: PropTypes.string.isRequired,
  isLoadingAI: PropTypes.bool.isRequired,
  showListContactsEmptyState: PropTypes.bool,
  listAction: PropTypes.node,
  type: PropTypes.string,
  disabledContacts: PropTypes.arrayOf(PropTypes.number),
  otherToolbarOptions: PropTypes.node,
  tableSx: PropTypes.object,
  cardSx: PropTypes.object,
  onOpenNotesDrawer: PropTypes.func,
};
